/**
 * Graph searches over the hex board shared by several stages: BFS distance fields, multi-source
 * weighted region growth (territories, regions) and A* (roads, rails).
 */
import type { HexGrid, HexId } from '../core/hex';
import { MinHeap } from './heap';

/** Cost of stepping from `from` into its neighbour `to` (direction `dir`). Infinity = blocked. */
export type StepCost = (from: HexId, to: HexId, dir: number) => number;

/**
 * Multi-source BFS. Returns step distance per hex (-1 = unreachable) and, in `label`, the index
 * (into `sources`) of the source that reached the hex first — i.e. a geodesic Voronoi partition.
 */
export function bfs(
  grid: HexGrid,
  sources: readonly HexId[],
  passable: Uint8Array,
): { dist: Int32Array; label: Int32Array } {
  const n = grid.count;
  const dist = new Int32Array(n).fill(-1);
  const label = new Int32Array(n).fill(-1);
  const queue = new Int32Array(n);
  let head = 0;
  let tail = 0;
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i];
    if (dist[s] !== -1) continue;
    dist[s] = 0;
    label[s] = i;
    queue[tail++] = s;
  }
  const nb = grid.neighbours;
  while (head < tail) {
    const h = queue[head++];
    const d = dist[h] + 1;
    for (let k = 0; k < 6; k++) {
      const m = nb[h * 6 + k];
      if (m < 0 || dist[m] !== -1 || !passable[m]) continue;
      dist[m] = d;
      label[m] = label[h];
      queue[tail++] = m;
    }
  }
  return { dist, label };
}

/**
 * Multi-source Dijkstra "growth": every source floods outwards, its path costs multiplied by its
 * own weight (a slower-growing source ends up with less). Each hex inherits the label of the hex it
 * was reached from, so every label's area is contiguous by construction — the property territories
 * and regions need. Only hexes with `allowed[h] != 0` are claimed; others stay -1.
 */
export function growRegions(
  grid: HexGrid,
  sources: readonly HexId[],
  weights: readonly number[],
  cost: StepCost,
  allowed: Uint8Array,
): { label: Int32Array; dist: Float64Array } {
  const n = grid.count;
  const dist = new Float64Array(n).fill(Infinity);
  const label = new Int32Array(n).fill(-1);
  const heap = new MinHeap(n);
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i];
    dist[s] = 0;
    label[s] = i;
    heap.push(s, 0);
  }
  const nb = grid.neighbours;
  while (heap.size > 0) {
    const h = heap.pop();
    if (heap.lastKey > dist[h]) continue;
    const l = label[h];
    const w = weights[l];
    for (let d = 0; d < 6; d++) {
      const m = nb[h * 6 + d];
      if (m < 0 || !allowed[m]) continue;
      const nd = dist[h] + cost(h, m, d) * w;
      if (nd < dist[m]) {
        dist[m] = nd;
        label[m] = l;
        heap.push(m, nd);
      }
    }
  }
  return { label, dist };
}

/**
 * A* over the hex board with reusable buffers (roads and rails run ~100 searches per map).
 * Buffers are invalidated with a generation stamp instead of being refilled per search.
 */
export class PathFinder {
  private readonly grid: HexGrid;
  private readonly g: Float64Array;
  private readonly prev: Int32Array;
  private readonly stamp: Uint32Array;
  private readonly closed: Uint32Array;
  private readonly q: Float64Array;
  private readonly r: Float64Array;
  private readonly heap: MinHeap;
  private gen = 0;

  constructor(grid: HexGrid) {
    this.grid = grid;
    const n = grid.count;
    this.g = new Float64Array(n);
    this.prev = new Int32Array(n);
    this.stamp = new Uint32Array(n);
    this.closed = new Uint32Array(n);
    this.q = new Float64Array(n);
    this.r = new Float64Array(n);
    this.heap = new MinHeap(n);
    for (let h = 0; h < n; h++) {
      const [q, r] = grid.toAxial(h);
      this.q[h] = q;
      this.r[h] = r;
    }
  }

  /** Hex distance via cached axial coordinates. */
  distance(a: HexId, b: HexId): number {
    const dq = this.q[a] - this.q[b];
    const dr = this.r[a] - this.r[b];
    return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
  }

  /**
   * Cheapest path from `from` to `to` (both included), or null if unreachable. `minStep` must be a
   * lower bound of any step cost so the heuristic stays admissible.
   */
  find(from: HexId, to: HexId, cost: StepCost, minStep: number): HexId[] | null {
    const gen = ++this.gen;
    const { g, prev, stamp, closed, heap } = this;
    const nb = this.grid.neighbours;
    heap.clear();
    g[from] = 0;
    prev[from] = -1;
    stamp[from] = gen;
    heap.push(from, this.distance(from, to) * minStep);
    while (heap.size > 0) {
      const h = heap.pop();
      if (closed[h] === gen) continue;
      closed[h] = gen;
      if (h === to) {
        const path: HexId[] = [];
        for (let c = to; c !== -1; c = prev[c]) path.push(c);
        return path.reverse();
      }
      for (let d = 0; d < 6; d++) {
        const m = nb[h * 6 + d];
        if (m < 0 || closed[m] === gen) continue;
        const step = cost(h, m, d);
        if (step === Infinity) continue;
        const ng = g[h] + step;
        if (stamp[m] !== gen || ng < g[m]) {
          stamp[m] = gen;
          g[m] = ng;
          prev[m] = h;
          heap.push(m, ng + this.distance(m, to) * minStep);
        }
      }
    }
    return null;
  }
}
